import React, { useContext, useEffect, useState } from "react";
import "../../components/login/login.css";
import {
  Alert,
  Box,
  Card,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Dialog,
  DialogContent,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "../../constants/constants";
import { LoadingButton } from "@mui/lab";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import PasswordReset from "../../components/login/PasswordReset";
import axios from "axios";
import { getDbHeader, getErrorResponse } from "../../utils2024/utils";
import { useNavigate } from "react-router-dom";
import { saveToken, useQuery } from "../../utility/utility";
import useGetEventSettings from "../hooks/useGetEventSettings";
import useGetRegLinks from "../hooks/useGetRegLinks"; // <-- Import the new hook
import MuPb from "../../widgets/MuPb";
import { UserContext } from "../context/UserContext";

const EventLogin = () => {
  const { reloadUser } = useContext(UserContext);
  const { data, isLoading, isError, error: loadError } = useGetEventSettings();

  // Fetch registration links
  const { data: regLinksData } = useGetRegLinks();

  const navigate = useNavigate();
  const queryParams = useQuery();
  const actionTab = queryParams.get("a");
  const [error, setError] = useState();
  const [isLoggingIn, setIsLoggingIn] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberChecked, setRememberChecked] = useState(false);

  useEffect(() => {
    if (actionTab === "activate") {
      setShowPasswordReset(true);
    }
  }, [actionTab]);

  const onLogin = async () => {
    if (username === "") {
      toast.error("Enter username");
    } else if (password === "") {
      toast.error("Enter password");
    } else {
      try {
        setIsLoggingIn(true);
        const { data } = await axios.post(
          `${API_ENDPOINT}/user/login`,
          { username, password },
          getDbHeader()
        );
        saveToken(data.data.token);
        reloadUser();
        navigate(`/${data.data.role}`);
      } catch (error) {
        setError(error?.response?.data);
      } finally {
        setIsLoggingIn(false);
      }
    }
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const rememberPwd = () => {
    setRememberChecked((prev) => !prev);
  };

  const handleForgotPwd = () => {
    setShowPasswordReset(true);
  };

  // Find the dynamically loaded template1Link
  // Works if backend returns { template1Link: "..." } OR the raw { registrationLinks: [...] } array
  const dynamicTemplate1Link =
    regLinksData?.template1Link ||
    regLinksData?.registrationLinks?.find((l) => l.template === "template1")
      ?.preview ||
    "#";

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return <Alert severity="error">{getErrorResponse(loadError)}</Alert>;
  }

  return (
    <div className="expp-login-ca">
      <Dialog
        open={showPasswordReset}
        onClose={() => setShowPasswordReset(false)}
        aria-labelledby="password-reset-dialog-title"
        aria-describedby="password-reset-dialog-description"
        sx={{
          "& .MuiDialog-paper": {
            width: "500px",
            maxWidth: "90vw",
          },
        }}
      >
        <DialogContent>
          <PasswordReset
            show={showPasswordReset}
            setShow={setShowPasswordReset}
            banner={data?.eventBanner}
          />
        </DialogContent>
      </Dialog>
      <div className="login-box">
        <Card
          sx={{
            borderRadius: 0,
            backgroundImage: `url(${data?.loginBanner})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
          className="login-flex-left"
        ></Card>
        <div className="login-flex-right">
          <div className="login-action-box">
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box>
                <Box
                  sx={{
                    textAlign: "center",
                    fontSize: "20px",
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Welcome to
                </Box>
                <img
                  src={
                    "https://s3.ap-south-1.amazonaws.com/expoplanner.in/download.png"
                  }
                  alt=""
                />
              </Box>
            </div>
            {error && <Alert severity="error">{error.message}</Alert>}
            <div style={{ marginTop: "28px" }}>
              <TextField
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                variant="outlined"
                size="small"
                label="Username"
                fullWidth
              />
              <FormControl
                variant="outlined"
                size="small"
                required
                fullWidth
                sx={{ marginTop: 3 }}
              >
                <InputLabel htmlFor="standard-adornment-password">
                  Password
                </InputLabel>
                <OutlinedInput
                  id="standard-adornment-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {!showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                />
              </FormControl>

              <div style={{ marginTop: "28px" }}>
                <div style={{ display: "flex" }}>
                  <FormControlLabel
                    sx={{
                      flex: 1,
                      color: "#777",
                      "& .MuiTypography-root": {
                        fontSize: "15px",
                      },
                      "& .MuiCheckbox-root": {
                        "& .MuiSvgIcon-root": {
                          fontSize: "18px",
                        },
                      },
                    }}
                    label="Remember Password"
                    control={
                      <Checkbox
                        checked={rememberChecked}
                        onChange={rememberPwd}
                      />
                    }
                  />
                  <div
                    style={{
                      textDecoration: "underline",
                      fontSize: "15px",
                      alignSelf: "center",
                      display: "flex",
                      justifyContent: "end",
                      cursor: "pointer",
                      color: "#888",
                    }}
                    onClick={handleForgotPwd}
                  >
                    Forgot your password?
                  </div>
                </div>

                <div style={{ marginTop: "16px" }}>
                  <LoadingButton
                    onClick={onLogin}
                    loading={isLoggingIn}
                    fullWidth
                    sx={{
                      backgroundColor: "#054054",
                      "&:hover": {
                        backgroundColor: "#0B6B6B",
                      },
                    }}
                    variant="contained"
                  >
                    Sign in
                  </LoadingButton>
                </div>

                {/* Dynamically conditionally rendering the registration link if one is available */}
                {dynamicTemplate1Link !== "#" && (
                  <div style={{ marginTop: "16px", textAlign: "center" }}>
                    <span style={{ fontSize: "15px", color: "#888" }}>
                      Haven't registered yet?{" "}
                    </span>
                    <a
                      href={dynamicTemplate1Link}
                      style={{
                        color: "#054054",
                        fontWeight: "bold",
                        textDecoration: "none",
                      }}
                    >
                      Register now
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventLogin;
